<script lang="ts" setup>
import { useMagicKeys } from '@vueuse/core'
import { MagnifyIcon } from 'mdi-vue3'
import { computed, type Ref, ref, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'

import { isMac } from '../../../lib/utils'

const { t } = useI18n({
  messages: {
    en: {
      search: 'Search',
    },
    nl: {
      search: 'Zoeken',
    },
    de: {
      search: 'Suchen',
    },
  },
})

const {
  ctrl: ctrlKey,
  meta: metaKey,
  s: sKey,
} = useMagicKeys({
  passive: false,
  onEventFired(event: KeyboardEvent) {
    if ((event.ctrlKey || event.metaKey) && event.key === 's' && event.type === 'keydown') event.preventDefault()
  },
})

const query = defineModel<string>({ default: '', required: true })

const input: Ref<HTMLInputElement | undefined> = ref(undefined)

defineOptions({ inheritAttrs: false })

watchEffect(() => {
  if ((ctrlKey.value || metaKey.value) && sKey.value) {
    console.log('CMD + S have been pressed')
    focus()
  }
})

function focus() {
  if (input.value) {
    input.value.focus()
  }

  emit('focus')
}

const shortcutLabel = computed(() => {
  return isMac() ? '⌘S' : 'Ctrl+S'
})

export type TableSearchProperties = {
  disabled?: boolean
}

withDefaults(defineProps<TableSearchProperties>(), {
  disabled: false,
})

interface TableSearchEmits {
  /**
   * Emitted when the button is clicked
   */
  (event: 'input', value: string): void
  (event: 'focus'): void
}

const emit = defineEmits<TableSearchEmits>()
</script>

<template>
  <div class="atabix-table-search">
    <MagnifyIcon class="atabix-table-search-icon" />
    <input
      ref="input"
      v-model="query"
      :aria-label="t('search')"
      class="atabix-table-search-input"
      :disabled="disabled"
      maxlength="75"
      :placeholder="t('search')"
      type="search"
    />
    <span class="atabix-table-search-shortcut">{{ shortcutLabel }}</span>
  </div>
</template>

<style lang="scss">
.atabix-table-search {
  @apply bg-background;
  @apply flex;
  @apply items-center;
  @apply border-border;
  @apply border;
  @apply rounded;
  @apply h-[40px];
  @apply w-[396px];
  @apply my-0;
  @apply py-0;
  @apply relative;
  @apply select-none;
}

.atabix-table-search-icon {
  @apply h-5;
  @apply text-form-foreground-val-placeholder;
  @apply absolute;
  @apply left-0;
  @apply px-2;
}

.atabix-table-search-input {
  @apply text-base;
  @apply px-8;
  @apply leading-5;
  @apply w-full;
  @apply h-full;
  @apply flex;
  @apply items-center;
  @apply rounded;
  @apply bg-transparent;

  @apply focus:outline-dashed;
  @apply focus:border-0;
  @apply focus:outline-offset-4;
  @apply focus:outline-border;

  &::placeholder {
    @apply text-form-foreground-val-placeholder;
  }
}

.atabix-table-search-shortcut {
  @apply absolute;
  @apply pr-2;
  @apply right-0;
  @apply text-base;
  @apply text-form-foreground-helptext;
  @apply tracking-widest;
}
</style>
